import { useState } from 'react'
import Swal from 'sweetalert2'
import toast from 'react-hot-toast'
import Section from '@/components/Section'
import { TOOLS } from '@/data/landing'
import type { Tool } from '@/types'

function handleToolClick(tool: Tool) {
  const action = tool.action || 'link'

  if (action === 'youtube') {
    Swal.fire({
      title: tool.name,
      html: `
        <div style="position:relative;padding-bottom:56.25%;height:0;overflow:hidden;border-radius:8px;">
          <iframe
            src="https://www.youtube.com/embed/${tool.youtubeId}?autoplay=1&rel=0"
            style="position:absolute;top:0;left:0;width:100%;height:100%;border:none;"
            allow="autoplay; encrypted-media"
            allowfullscreen
          ></iframe>
        </div>
      `,
      width: 720,
      showConfirmButton: false,
      showCloseButton: true,
      background: '#1C1228',
      color: '#fff',
      customClass: { popup: 'rounded-2xl' },
    })
    return
  }

  if (action === 'apps') {
    Swal.fire({
      title: tool.name,
      html: `
        <p style="color:#6B5F7B;font-size:15px;margin-bottom:20px;">
          Daily affirmations across 8 life areas with gentle reminders throughout your day.
        </p>
        <p style="color:#9589A3;font-size:13px;margin-bottom:16px;">Available on iOS & Android</p>
        <div style="display:flex;gap:12px;justify-content:center;flex-wrap:wrap;">
          <a href="https://apps.apple.com/us/app/daily-affirmation-flow/id6755495993" target="_blank" rel="noopener noreferrer"
            style="display:inline-flex;align-items:center;gap:8px;padding:12px 24px;background:#000;color:#fff;border-radius:10px;text-decoration:none;font-size:14px;font-weight:600;">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
            App Store
          </a>
          <a href="https://play.google.com/store/apps/details?id=com.charsoft.affirmationflow" target="_blank" rel="noopener noreferrer"
            style="display:inline-flex;align-items:center;gap:8px;padding:12px 24px;background:#000;color:#fff;border-radius:10px;text-decoration:none;font-size:14px;font-weight:600;">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="white"><path d="M3.18 23.77c.55.31 1.22.3 1.76-.03l12.89-7.24-3.1-3.1L3.18 23.77zM2.27 2.84C2.1 3.12 2 3.45 2 3.82v16.36c0 .37.1.7.27.98l8.83-8.84L2.27 2.84zM21.17 10.65l-3.2-1.8-3.38 3.38 3.38 3.38 3.2-1.8c.91-.51.91-1.79 0-2.3v-.86zM4.94.22L17.83 7.46l-3.1 3.1L3.18.23C3.73-.1 4.4-.11 4.94.22z"/></svg>
            Google Play
          </a>
        </div>
      `,
      showConfirmButton: false,
      showCloseButton: true,
      background: '#fff',
      customClass: { popup: 'rounded-2xl' },
    })
    return
  }

  if (action === 'request-access') {
    Swal.fire({
      html: `
        <div style="text-align:center;">
          <div style="font-size:56px;animation:modalPulse 0.6s ease-out;">${tool.emoji}</div>
          <h2 style="font-size:22px;font-weight:700;color:#2D2042;margin:16px 0 8px;">${tool.name}</h2>
          <p style="color:#6B5F7B;font-size:15px;margin-bottom:20px;">
            This tool is coming soon! Join the waitlist and we'll notify you when it's ready.
          </p>
          <form id="waitlist-form" style="display:flex;gap:8px;max-width:400px;margin:0 auto;">
            <input type="email" id="waitlist-email" placeholder="Your email address" required
              style="flex:1;padding:12px 16px;border:1px solid #d4cce5;border-radius:10px;font-size:15px;outline:none;" />
            <button type="submit"
              style="padding:12px 24px;background:linear-gradient(135deg,#D4A843,#C9962C);color:#fff;border:none;border-radius:10px;font-weight:700;font-size:15px;cursor:pointer;white-space:nowrap;">
              Join Waitlist
            </button>
          </form>
          <p style="color:#9589A3;font-size:12px;margin-top:12px;">
            No spam. Just a heads-up when it launches.
          </p>
        </div>
      `,
      showConfirmButton: false,
      showCloseButton: true,
      background: '#fff',
      customClass: { popup: 'rounded-2xl' },
      didOpen: () => {
        const form = document.getElementById('waitlist-form') as HTMLFormElement
        form?.addEventListener('submit', async (e) => {
          e.preventDefault()
          const emailInput = document.getElementById('waitlist-email') as HTMLInputElement
          const email = emailInput?.value
          if (!email) return
          const btn = form.querySelector('button') as HTMLButtonElement
          btn.textContent = '...'
          btn.disabled = true
          try {
            const resp = await fetch('/api/leads', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email, source: '${tool.name}', type: 'waitlist' }),
            })
            if (!resp.ok) throw new Error('Failed')
            Swal.close()
            toast.success("You're on the list! We'll email you when it's ready.")
          } catch {
            btn.textContent = 'Join Waitlist'
            btn.disabled = false
            toast.error('Something went wrong. Please try again.')
          }
        })
      },
    })
    return
  }
}

export default function ToolsSection() {
  const [expanded, setExpanded] = useState(false)

  return (
    <div id="tools" className="py-24 px-6 max-w-[960px] mx-auto">
      <Section>
        <div className="text-center mb-14">
          <span className="inline-block py-1.5 px-4 rounded-full bg-brand-mid/10 text-brand-mid text-[11px] font-semibold tracking-[2px] uppercase mb-4">
            SaaS Tools
          </span>
          <h2 className="font-display text-[clamp(30px,5vw,44px)] font-bold text-text-dark leading-[1.15] mb-4">
            Built for My Center.<br />Ready for Yours.
          </h2>
          <p className="text-text-gray text-[17px] max-w-[540px] mx-auto leading-relaxed">
            Every tool here was born from a real need at my own spiritual center.
            They're designed for spiritual leaders — not tech people.
          </p>
        </div>
      </Section>

      <div className="relative">
        <div className={`overflow-hidden transition-all duration-500 ${expanded ? 'max-h-[2200px]' : 'max-h-[500px] md:max-h-[540px]'}`}>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-5">
            {TOOLS.map((tool, i) => {
              const isModal = tool.action && tool.action !== 'link'
              const ctaStyle = {
                background: tool.color + '10',
                color: tool.color,
                border: `1px solid ${tool.color}20`,
              }

              return (
                <Section key={tool.id} delay={`${i * 0.1}s`}>
                  <div className="bg-card-bg rounded-2xl p-8 border border-card-border h-full flex flex-col transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_12px_40px_rgba(28,18,40,0.08)]">
                    <div className="flex items-center gap-3.5 mb-4">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center text-[28px]"
                        style={{ background: tool.color + '10' }}
                      >
                        {tool.emoji}
                      </div>
                      <div>
                        <div className="font-bold text-[17px] text-text-dark">{tool.name}</div>
                        <div
                          className="text-[10px] font-bold tracking-[1px] uppercase"
                          style={{ color: tool.color }}
                        >
                          {tool.tag}
                        </div>
                      </div>
                    </div>
                    <p className="text-text-gray text-[15px] leading-relaxed flex-1 mb-5">
                      {tool.desc}
                    </p>
                    {isModal ? (
                      <button
                        onClick={() => handleToolClick(tool)}
                        className="inline-block py-2.5 px-5 rounded-full text-sm font-semibold text-center transition-all duration-200 hover:scale-[1.02] cursor-pointer"
                        style={ctaStyle}
                      >
                        {tool.cta} →
                      </button>
                    ) : (
                      <a
                        href={tool.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block py-2.5 px-5 rounded-full text-sm font-semibold no-underline text-center transition-all duration-200 hover:scale-[1.02]"
                        style={ctaStyle}
                      >
                        {tool.cta} →
                      </a>
                    )}
                  </div>
                </Section>
              )
            })}
          </div>
        </div>

        {!expanded ? (
          <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-[#F9F7FC] via-[#F9F7FC]/92 to-transparent" />
        ) : null}
      </div>

      <div className="text-center mt-8">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="inline-block py-2.5 px-6 rounded-full border border-brand-mid/25 bg-white text-brand-mid text-sm font-semibold transition-all duration-200 hover:scale-[1.02] hover:bg-brand-mid/5"
        >
          {expanded ? 'View Less' : 'View More'}
        </button>
      </div>
    </div>
  )
}
